import { Button, LinkButton } from '@/atoms/Button'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { Image } from '@/atoms/Image'
import { CaptionSM, HeadingXS, ParagraphLG, TitleLG } from '@/atoms/Text'
import { TicketsLayout } from '@/layouts/TicketsLayout'
import { TheatricalReleaseResult } from '@/services/Theatrical'
import { useLocale } from '@/utils/LocaleUtil'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

interface Props {
  onClick: () => void
  theatricalRelease: TheatricalReleaseResult
}

export const FreeTicketClaimPartnerView = ({ onClick, theatricalRelease }: Props) => {
  const { t } = useTranslate('tickets')
  const { locale } = useLocale()

  const theatricalReleaseRegion = theatricalRelease?.region
  if (!theatricalRelease || !theatricalReleaseRegion) {
    return null
  }

  const { backdropImage, shortDescription, title, backdropMobileImage, logoImageWeb } = theatricalRelease
  const { releaseDate, partnerUrl } = theatricalReleaseRegion

  const backdropImageUrl = 'https://images.angelstudios.com/image/upload/' + backdropImage

  return (
    <TicketsLayout shouldShowLanguageModal title={t('freeTicket', 'Free Ticket')} isDarkMode={true}>
      <div className="relative mx-auto mt-2 w-[95%] max-w-7xl rounded-2xl">
        <Image
          src={backdropMobileImage ?? ''}
          alt={`${title} background image`}
          width={375}
          height={375}
          className="mx-auto rounded-2xl md:hidden"
        />
        <div
          className="mx-auto hidden h-[420px] w-full max-w-7xl rounded-2xl bg-cover bg-top md:block"
          style={{ backgroundImage: `url('${backdropImageUrl}')` }}
        />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-center text-white md:bottom-1/2 md:left-1/4 md:-translate-x-1/4 md:translate-y-1/2">
          <div className="aspect-h-1 aspect-w-2 relative mx-auto mb-4">
            <Image src={logoImageWeb ?? ''} alt={shortDescription ?? ''} fill={true} className="object-fit" />
          </div>

          <CaptionSM color="white" className="mx-auto my-4 w-fit rounded-full bg-black/30 px-3 py-1">
            {new Date(releaseDate).toLocaleDateString(locale, {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          </CaptionSM>
          <TitleLG weight="bold" className="text-nowrap">
            {t('comingSoonToTheaters', 'Coming soon to Theaters')}
          </TitleLG>
          {typeof partnerUrl === 'string' && (
            <LinkButton
              href={partnerUrl}
              variant="white"
              className="mx-auto my-8 flex flex-row gap-[6px]"
              target="_blank"
              external
            >
              <TicketIcon color="core-gray-950" />
              {t('getShowtimes', 'Get Showtimes')}
            </LinkButton>
          )}
        </div>
      </div>
      <div className="mx-auto max-w-screen-sm md:my-20">
        <div className="text-center">
          <HeadingXS color="white" weight="bold" className="my-6">
            {t('claimYourFreeTicket', 'Claim Your Free Ticket')}
          </HeadingXS>
          <ParagraphLG color="core-gray-400">
            <Translate t={t} i18nKey={'PIFTicketClaimMessageAngel'} values={{ title }}>
              Enjoy a free ticket to watch {{ title }} in theaters, courtesy of Angel and those who contributed to Pay
              it Forward.
            </Translate>
          </ParagraphLG>
        </div>
        <Button
          onClick={() => onClick()}
          variant="white"
          className="mx-auto my-8 flex w-[95%] flex-row gap-[6px] md:w-fit"
        >
          <TicketIcon color="core-gray-950" />
          {t('claimAFreeTicket', 'Claim a Free Ticket')}
        </Button>
        <div className="my-12 flex flex-col items-center justify-center gap-3 md:flex-row">
          <AngelLogo />
        </div>
      </div>
    </TicketsLayout>
  )
}

const AngelLogo = ({ className }: { className?: string }) => (
  <svg
    width="167"
    height="60"
    viewBox="0 0 167 60"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M165.479 35.8622L153.252 33.2012V7.90558C153.252 7.6698 153.17 7.43402 153.02 7.24877C152.871 7.06351 152.656 6.94562 152.424 6.8951L147.09 6.15408C146.891 6.13724 146.675 6.20461 146.526 6.33934C146.377 6.47407 146.278 6.65932 146.261 6.87826C146.261 6.8951 146.261 6.92878 146.261 6.94562V37.7821C146.278 38.0179 146.361 38.2368 146.51 38.4052C146.659 38.5736 146.874 38.6915 147.09 38.742L165.479 42.5819C165.578 42.5987 165.678 42.5819 165.76 42.565C165.86 42.5482 165.943 42.4977 166.025 42.4303C166.108 42.3629 166.175 42.2956 166.208 42.2113C166.257 42.1271 166.291 42.0261 166.307 41.9419C166.307 41.9082 166.307 41.8745 166.307 41.8408V36.8726C166.274 36.6369 166.175 36.4179 166.025 36.2495C165.893 36.0811 165.694 35.9295 165.479 35.8622ZM134.101 4.80678L115.546 3.27422C115.43 3.25738 115.331 3.27422 115.215 3.3079C115.115 3.34158 115.016 3.39211 114.933 3.47631C114.85 3.54368 114.784 3.64473 114.734 3.74577C114.685 3.84682 114.652 3.94787 114.652 4.06576V34.2791C114.668 34.5149 114.768 34.7338 114.933 34.9022C115.099 35.0706 115.314 35.1717 115.546 35.1885L134.101 36.7042C134.201 36.7211 134.3 36.7042 134.399 36.6706C134.499 36.6369 134.582 36.5863 134.664 36.519C134.747 36.4516 134.797 36.3674 134.847 36.2832C134.896 36.199 134.913 36.0979 134.929 35.9801C134.929 35.9632 134.929 35.9295 134.929 35.9127V30.9445C134.913 30.7087 134.83 30.4898 134.681 30.3214C134.532 30.153 134.316 30.0351 134.101 29.9845L121.659 28.9067V22.7091L131.865 23.6691C132.063 23.7027 132.246 23.6354 132.411 23.5175C132.56 23.3996 132.676 23.2143 132.693 23.0291C132.693 22.9786 132.693 22.9449 132.693 22.8944V17.9093C132.676 17.6735 132.594 17.4546 132.444 17.2862C132.295 17.1178 132.096 16.9999 131.865 16.9494L121.643 16.0399V10.3981L134.085 11.5265C134.184 11.5265 134.283 11.5265 134.383 11.4928C134.482 11.4591 134.565 11.4086 134.648 11.3412C134.731 11.2738 134.78 11.1896 134.83 11.1054C134.88 11.0212 134.896 10.9202 134.913 10.8023C134.913 10.7854 134.913 10.7518 134.913 10.7349V5.76673C134.896 5.53095 134.813 5.31202 134.664 5.1436C134.515 4.97519 134.316 4.8573 134.101 4.80678ZM102.326 15.5852H91.0604C90.9444 15.5684 90.8284 15.5852 90.7125 15.6189C90.5965 15.6526 90.4971 15.72 90.4143 15.8042C90.3314 15.8884 90.2652 15.9894 90.2155 16.0905C90.1658 16.1915 90.1492 16.3094 90.1492 16.4273V21.2439C90.1492 21.3618 90.1658 21.4797 90.2155 21.5807C90.2652 21.6818 90.3314 21.7828 90.4143 21.867C90.4971 21.9512 90.5965 22.0018 90.6959 22.0523C90.8119 22.086 90.9113 22.1028 91.0272 22.1028C91.9219 22.1028 95.1358 22.1028 96.1298 22.1533V26.7679C94.8376 27.2226 93.4957 27.4247 92.1372 27.391C86.637 27.2731 83.2574 23.2143 83.2574 18.1956C83.2574 13.2274 86.637 8.98342 92.0875 9.10131C94.2578 9.15184 96.2624 9.77497 97.9688 11.4759C98.151 11.5938 98.3664 11.6612 98.5817 11.6612C98.7971 11.6612 99.0125 11.5938 99.1947 11.4759L102.69 7.75401C102.773 7.6698 102.839 7.56875 102.889 7.4677C102.939 7.34982 102.955 7.23193 102.955 7.11404C102.955 6.99615 102.922 6.87826 102.856 6.77721C102.806 6.67616 102.723 6.57512 102.624 6.50775C99.6089 3.67841 95.434 1.97744 91.6568 1.87639C82.7107 1.7585 75.5538 9.16868 75.5538 18.2461C75.5538 27.3405 82.7107 34.397 91.6568 34.6159C95.55 34.6833 99.3935 33.7738 102.839 31.955C102.939 31.8708 103.022 31.7697 103.071 31.6518C103.121 31.534 103.154 31.3992 103.154 31.2813V16.4947C103.154 16.2589 103.055 16.0399 102.906 15.8884C102.757 15.72 102.541 15.6189 102.326 15.5852ZM65.829 2.49952L60.4448 2.66793C60.1963 2.68477 59.9809 2.78582 59.8153 2.97107C59.6496 3.15633 59.5502 3.39211 59.5502 3.62788V19.8292L42.3372 4.01523C41.3432 3.1058 39.67 3.39211 39.67 4.41943V35.0875C39.6865 35.3064 39.7694 35.5085 39.935 35.6601C40.1007 35.8116 40.3161 35.879 40.5314 35.879H40.5646L45.8991 35.4917C46.131 35.4748 46.3464 35.3569 46.5121 35.1717C46.6612 34.9864 46.744 34.7675 46.744 34.5149V17.522H46.7937L64.5037 34.6833C64.951 35.0875 66.674 35.0875 66.674 34.0096V3.34158C66.674 3.24053 66.6574 3.12264 66.6243 3.0216C66.5911 2.92055 66.5248 2.8195 66.4586 2.75214C66.3923 2.66793 66.2929 2.61741 66.1935 2.56688C66.0776 2.51636 65.9616 2.49952 65.8622 2.49952H65.829ZM9.2532 40.5946C9.33603 40.5609 9.4023 40.5272 9.452 40.4767C9.51827 40.4262 9.5514 40.3588 9.58454 40.2746L17.4207 22.7091L17.5366 22.5575L17.6526 22.7091L21.2642 31.0624L16.1119 32.1234C15.4326 32.2581 15.4823 33.016 16.145 33.3528L24.4616 37.5968C24.7101 37.7315 25.0083 37.7821 25.2899 37.7652L30.873 37.0579C31.3368 36.9569 31.8338 36.6537 31.8504 36.1653C31.8504 35.9969 31.8173 35.8453 31.751 35.6938L18.6797 7.1814C18.6797 7.1814 18.0502 6.6088 17.5366 6.6088C17.3213 6.59196 17.0893 6.64248 16.8905 6.74353C16.6917 6.84458 16.5261 6.99615 16.3935 7.1814L0.737829 41.2851C0.671561 41.4366 0.638428 41.5882 0.638428 41.7398C0.638428 41.8745 0.654995 41.9924 0.704695 42.1103C0.754396 42.2282 0.820663 42.3292 0.920064 42.4134C1.01947 42.4976 1.11887 42.565 1.23483 42.5987C1.3508 42.6324 1.48334 42.6492 1.59931 42.6492C1.84781 42.6492 9.2532 40.5946 9.2532 40.5946Z"
      fill="white"
    />
  </svg>
)
